import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';

const BASE = '/api/v1/analytics';

export function useDashboardKpis(period: 'today' | 'week' | 'month' = 'today') {
  const { get, loading } = useApi({ showErrorToast: false });
  const [data, setData] = useState<Record<string, any> | null>(null);

  const fetch = useCallback(async () => {
    try {
      const res = await get(BASE + '/kpis', { period });
      setData(res);
    } catch {}
  }, [period]); // eslint-disable-line

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, refetch: fetch };
}

export function useShipmentTrend(days = 30) {
  const { get, loading } = useApi({ showErrorToast: false });
  const [data, setData] = useState<any[]>([]);

  const fetch = useCallback(async () => {
    try {
      const res = await get(BASE + '/shipment-trend', { days });
      setData(Array.isArray(res) ? res : []);
    } catch {}
  }, [days]); // eslint-disable-line

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading };
}

export function useRevenueTrend(days = 30) {
  const { get, loading } = useApi({ showErrorToast: false });
  const [data, setData] = useState<any[]>([]);

  const fetch = useCallback(async () => {
    try {
      const res = await get(BASE + '/revenue-trend', { days });
      setData(Array.isArray(res) ? res : []);
    } catch {}
  }, [days]); // eslint-disable-line

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading };
}

export function useStatusDistribution() {
  const { get, loading } = useApi({ showErrorToast: false });
  const [data, setData] = useState<any[]>([]);

  const fetch = useCallback(async () => {
    try {
      const res = await get(BASE + '/status-distribution');
      setData(Array.isArray(res) ? res : []);
    } catch {}
  }, []); // eslint-disable-line

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading };
}

export function useTopCities(limit = 10, direction: 'to' | 'from' = 'to') {
  const { get, loading } = useApi({ showErrorToast: false });
  const [data, setData] = useState<any[]>([]);

  const fetch = useCallback(async () => {
    try {
      const res = await get(BASE + '/top-cities', { limit, direction });
      setData(Array.isArray(res) ? res : []);
    } catch {}
  }, [limit, direction]); // eslint-disable-line

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading };
}

export function useCarrierPerformance() {
  const { get, loading } = useApi({ showErrorToast: false });
  const [data, setData] = useState<any[]>([]);

  const fetch = useCallback(async () => {
    try {
      const res = await get(BASE + '/carrier-performance');
      setData(Array.isArray(res) ? res : []);
    } catch {}
  }, []); // eslint-disable-line

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading };
}

export function useDriverRankings(limit = 10) {
  const { get, loading } = useApi({ showErrorToast: false });
  const [data, setData] = useState<any[]>([]);

  const fetch = useCallback(async () => {
    try {
      const res = await get(BASE + '/driver-rankings', { limit });
      setData(Array.isArray(res) ? res : []);
    } catch {}
  }, [limit]); // eslint-disable-line

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading };
}

export function useHourlyDistribution() {
  const { get, loading } = useApi({ showErrorToast: false });
  const [data, setData] = useState<any[]>([]);

  const fetch = useCallback(async () => {
    try {
      const res = await get(BASE + '/hourly-distribution');
      setData(Array.isArray(res) ? res : []);
    } catch {}
  }, []); // eslint-disable-line

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading };
}

export function useDriversOverview() {
  const { get, loading } = useApi({ showErrorToast: false });
  const [data, setData] = useState<any[]>([]);

  const fetch = useCallback(async () => {
    try {
      const res = await get(BASE + '/drivers-overview');
      setData(Array.isArray(res) ? res : []);
    } catch {}
  }, []); // eslint-disable-line

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, refetch: fetch };
}

export function useAlerts() {
  const { get, loading } = useApi({ showErrorToast: false });
  const [data, setData] = useState<any[]>([]);

  const fetch = useCallback(async () => {
    try {
      const res = await get(BASE + '/alerts');
      setData(Array.isArray(res) ? res : []);
    } catch {}
  }, []); // eslint-disable-line

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, refetch: fetch };
}

export function useRecentActivity(limit = 10) {
  const { get, loading } = useApi({ showErrorToast: false });
  const [data, setData] = useState<any[]>([]);

  const fetch = useCallback(async () => {
    try {
      const res = await get(BASE + '/recent-activity', { limit });
      setData(Array.isArray(res) ? res : []);
    } catch {}
  }, [limit]); // eslint-disable-line

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading };
}

export function useTenantFinance() {
  const { get, loading } = useApi({ showErrorToast: false });
  const [data, setData] = useState<Record<string, any> | null>(null);

  const fetch = useCallback(async () => {
    try {
      const res = await get(BASE + '/tenant-finance');
      setData(res);
    } catch {}
  }, []); // eslint-disable-line

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading };
}
